import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import {
  AdministrationsMultiSelect,
  ExecRoleSelect,
  type PersonAddress,
  PositionAddressDialog,
  ReparentPositionDialog,
  ResponsibilityMultiSelect,
  RoleSelect,
  UsStateSelect,
  useExecRoleLuts,
  useExecPositionTypeLuts,
  useGlobalMessageHandler,
  useProtectedRoleHoldersLut,
  useUsRoleTypeLuts
} from 'components'
import { type ExecRole } from 'services'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { getPersonAddresses } from 'services/person/us/edit/personAddressService'
import { filterBuildingsForExecutive, isExecPositionRowVacant } from 'helpers'
import {
  CCO_OPTIONS,
  CONGRESSIONAL_LIAISON_OPTIONS,
  type CongressionalLiaisonCode,
  ElectCode,
  EXEC_APPOINTMENT_PRESIDENTS,
  EXEC_POS_TYPE_ELECTED,
  EXEC_POSITION_LEVELS,
  filterExecutiveGeneralRoles,
  formatExecStatusLabel,
  getExecPositionFieldErrors,
  getOccupancyStatusWarning,
  getStatusesForOccupancy,
  getStatusTitleWarning,
  isStateExecutiveRole
} from 'utils'

const DRAFT_ADDRESS_VALUE = -1

const formatAddressOption = (address: PersonAddress, roleCode: string): string => {
  const label = `${roleCode} #${address.displayOrder}`
  const location = [address.primary.addressOne, address.primary.city].filter(part => part != null && part !== '').join(', ')
  return location === '' ? label : `${label} — ${location}`
}

export type DraftExecutivePosition = Omit<ExecutivePosition, 'execRole'> & {
  execRole: ExecRole | null
  branchRoleId: number
}

interface ExecutivePositionFormProps {
  position: DraftExecutivePosition
  update: (changes: Partial<DraftExecutivePosition>) => void
  fieldsDisabled: boolean
  persistNewAddress?: (draft: PersonAddress) => Promise<PersonAddress | null>
  // When true (the update page), the Office/Department/Agency field becomes an
  // org picker so an editor can move the position to a different org (TI-4039).
  // The create page leaves it read-only — the org is fixed by the entry point.
  allowOrgChange?: boolean
  isVacant?: boolean
  disableTenureFields?: boolean
  holder?: React.ReactNode
  onAddressOptionsLoaded?: (addresses: PersonAddress[]) => void
  addressRequired?: boolean
  excludePositionId?: number
}

export const ExecutivePositionForm: React.FC<ExecutivePositionFormProps> = ({ position, update, fieldsDisabled, persistNewAddress, allowOrgChange = false, isVacant = false, disableTenureFields = false, holder, onAddressOptionsLoaded, addressRequired = false, excludePositionId = 0 }) => {
  const { showErrorMessage } = useGlobalMessageHandler()
  const { lutValues: execRoleLut } = useExecRoleLuts()
  const { lutValues: execPosTypeLut } = useExecPositionTypeLuts()
  const { lutValues: roleTypeLutValues } = useUsRoleTypeLuts()
  const { lutValues: protectedRoleHoldersLut } = useProtectedRoleHoldersLut()
  const [addressOptions, setAddressOptions] = useState<PersonAddress[]>([])
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressLoadFailed, setAddressLoadFailed] = useState(false)
  const [showAddressDialog, setShowAddressDialog] = useState(false)
  const [showReparentDialog, setShowReparentDialog] = useState(false)

  const personId = position.person.id > 0 ? position.person.id : null
  const generalRoleId = position.branchRoleId

  const handleNewAddress = (draft: PersonAddress): void => {
    setShowAddressDialog(false)
    if (persistNewAddress != null) {
      void persistNewAddress(draft).then(created => {
        if (created != null) {
          setAddressOptions(prev => [...prev, created])
          update({ addressId: created.id, address: null })
        }
      })
    } else {
      update({ address: draft, addressId: null })
    }
  }

  const handleAddressSelect = (value: string | number): void => {
    if (value === '') {
      update({ addressId: null, address: null })
    } else if (Number(value) !== DRAFT_ADDRESS_VALUE) {
      update({ addressId: Number(value), address: null })
    }
  }

  useEffect(() => {
    setAddressLoadFailed(false)
    if (personId == null) {
      setAddressOptions([])
      setAddressLoading(false)
      return
    }
    let cancelled = false
    setAddressLoading(true)
    void getPersonAddresses(personId, generalRoleId)
      .then(addrs => { if (!cancelled) setAddressOptions(addrs) })
      .catch(() => { if (!cancelled) { setAddressOptions([]); setAddressLoadFailed(true) } })
      .finally(() => { if (!cancelled) setAddressLoading(false) })
    return () => { cancelled = true }
  }, [personId, generalRoleId])

  useEffect(() => {
    onAddressOptionsLoaded?.(addressOptions)
  }, [addressOptions, onAddressOptionsLoaded])

  const tenureFieldsDisabled = fieldsDisabled || disableTenureFields
  const isElected = position.typeId === EXEC_POS_TYPE_ELECTED
  const titleWarning = getStatusTitleWarning(position.statusId, position.title)
  const titleError = position.title.trim() === ''
  const occupantIsVacant = isVacant || isExecPositionRowVacant(position.person)
  const isStatePosition = isStateExecutiveRole(generalRoleId)
  const electionEnabled = isElected && !occupantIsVacant
  const electionErrors = getExecPositionFieldErrors(position, occupantIsVacant)
  const requiredMark = electionEnabled ? ' *' : ''
  const electTypeLabel = `Elect Type${requiredMark}`
  const occupancyStatusWarning = getOccupancyStatusWarning(position.statusId, occupantIsVacant)
  const statusOptions = getStatusesForOccupancy(occupantIsVacant, position.statusId)
  const execRoleOptions = execRoleLut.execRoles.filter(r => r.role.id === position.branchRoleId)
  const currentScope = { stateId: position.state?.id ?? null, topOrgId: position.org.topId ?? null }
  const branchRole = roleTypeLutValues.roles.get(position.branchRoleId) ?? null

  const addressHint = fieldsDisabled || isVacant || addressLoading || position.addressId != null || position.address != null
    ? null
    : addressLoadFailed
      ? 'Could not load the existing addresses for this person — reload before adding one, to avoid creating a duplicate.'
      : addressOptions.length === 0
        ? 'This person has no address for this role yet — use “Add Address” to create one.'
        : addressRequired
          ? 'Required — select an address or use “Add Address” to create one.'
          : 'No address selected — choose one or use “Add Address” to create one.'

  return (
    <>
      <Grid container spacing={2} alignItems='flex-start'>
        <Grid item xs={holder != null ? 3 : 6}>
          <FormControl fullWidth>
            <RoleSelect
              value={branchRole}
              updateRole={(newRole) => {
                if (newRole == null) return
                const roleExists = execRoleLut.execRoles.some(r => r.role.id === newRole.id)
                if (roleExists) {
                  update({
                    branchRoleId: newRole.id,
                    execRole: null,
                    execRole2: null,
                    execRole3: null,
                    addressId: null,
                    address: null,
                    ...(isStatePosition !== isStateExecutiveRole(newRole.id) ? { levelId: null, presidentId: null, liaisonCode: 0 } : {})
                  })
                } else {
                  showErrorMessage(`No executive roles found for branch role "${newRole.code ?? newRole.id}".`)
                }
              }}
              getRoleFilter={filterExecutiveGeneralRoles}
              disabled={fieldsDisabled}
              label='Branch Role *'
            />
          </FormControl>
        </Grid>
        <Grid item xs={holder != null ? 4 : 6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              fullWidth
              label='Office / Department / Agency *'
              value={position.org.name ?? ''}
              disabled
            />
            {allowOrgChange && !fieldsDisabled && (
              <Tooltip title='Move to another org'>
                <span>
                  <IconButton
                    color='primary'
                    size='small'
                    aria-label='Move to another org'
                    onClick={() => { setShowReparentDialog(true) }}
                  >
                    <DriveFileMoveOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Grid>
        {holder != null && (
          <Grid item xs={5}>
            {holder}
          </Grid>
        )}
        <Grid item xs={4}>
          <FormControl fullWidth>
            <UsStateSelect
              value={position.state}
              updateState={(s) => { if (s != null) update({ state: s }) }}
              disabled={fieldsDisabled}
              onlyUSStates
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <ExecRoleSelect
            label='Executive Role *'
            value={position.execRole}
            options={execRoleOptions}
            disabled={fieldsDisabled}
            required
            onChange={(r) => { if (r != null) update({ execRole: r }) }}
            protectedHolders={protectedRoleHoldersLut.holders}
            currentScope={currentScope}
            excludePositionId={excludePositionId}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label='Title *'
            value={position.title}
            onChange={e => { update({ title: e.target.value }) }}
            disabled={fieldsDisabled}
            error={titleError}
            helperText={titleError ? 'Title is required.' : (titleWarning ?? ' ')}
            FormHelperTextProps={{ sx: { color: titleError ? 'error.main' : (titleWarning != null ? 'warning.main' : undefined) } }}
          />
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {addressLoading
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 56, flexGrow: 1 }}>
                  <CircularProgress size={24} />
                </Box>
                )
              : (
                <FormControl fullWidth disabled={fieldsDisabled}>
                  <InputLabel id='address-label'>{addressRequired ? 'Address *' : 'Address'}</InputLabel>
                  <Select
                    labelId='address-label'
                    label={addressRequired ? 'Address *' : 'Address'}
                    aria-describedby={addressHint != null ? 'address-hint' : undefined}
                    value={position.address != null ? DRAFT_ADDRESS_VALUE : (position.addressId ?? '')}
                    onChange={e => { handleAddressSelect(e.target.value) }}
                  >
                    <MenuItem value=''>None</MenuItem>
                    {position.address != null && (
                      <MenuItem value={DRAFT_ADDRESS_VALUE}>New address (unsaved)</MenuItem>
                    )}
                    {addressOptions.map(a => (
                      <MenuItem key={a.id} value={a.id}>
                        {formatAddressOption(a, branchRole?.code ?? '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}
            <Button
              variant='outlined'
              size='small'
              color='primary'
              startIcon={<AddIcon />}
              disabled={fieldsDisabled || addressLoading}
              onClick={() => { setShowAddressDialog(true) }}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Add Address
            </Button>
          </Box>
          {addressHint != null && (
            <FormHelperText id='address-hint' sx={{ color: 'warning.main' }}>{addressHint}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={2}>
          <FormControl fullWidth disabled={fieldsDisabled}>
            <InputLabel id='cco-label'>CCO</InputLabel>
            <Select
              labelId='cco-label'
              label='CCO'
              value={position.cco ?? 0}
              onChange={e => { update({ cco: Number(e.target.value) }) }}
            >
              {CCO_OPTIONS.map(o => (
                <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <ExecRoleSelect
            label='Executive Role 2'
            value={position.execRole2}
            options={execRoleOptions}
            disabled={fieldsDisabled}
            onChange={(r) => { update({ execRole2: r }) }}
            protectedHolders={protectedRoleHoldersLut.holders}
            currentScope={currentScope}
            excludePositionId={excludePositionId}
          />
        </Grid>
        <Grid item xs={3}>
          <ExecRoleSelect
            label='Executive Role 3'
            value={position.execRole3}
            options={execRoleOptions}
            disabled={fieldsDisabled}
            onChange={(r) => { update({ execRole3: r }) }}
            protectedHolders={protectedRoleHoldersLut.holders}
            currentScope={currentScope}
            excludePositionId={excludePositionId}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth disabled={fieldsDisabled}>
            <InputLabel id='position-type-label'>Position Type *</InputLabel>
            <Select
              labelId='position-type-label'
              label='Position Type *'
              value={execPosTypeLut.execPosTypes.some(t => t.id === position.typeId) ? position.typeId : ''}
              onChange={e => { update({ typeId: Number(e.target.value) }) }}
            >
              {execPosTypeLut.execPosTypes.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.desc}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth disabled={fieldsDisabled}>
            <InputLabel id='position-status-label'>Position Status *</InputLabel>
            <Select
              labelId='position-status-label'
              label='Position Status *'
              value={position.statusId}
              onChange={e => { update({ statusId: Number(e.target.value) }) }}
            >
              {statusOptions.map(([id, s]) => (
                <MenuItem key={id} value={id}>{formatExecStatusLabel(s)}</MenuItem>
              ))}
            </Select>
            <FormHelperText sx={{ color: occupancyStatusWarning != null ? 'warning.main' : undefined }}>
              {occupancyStatusWarning ?? ' '}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={disableTenureFields ? 'Start Date' : 'Start Date *'}
            value={position.apptStartDate ?? ''}
            onChange={e => { update({ apptStartDate: e.target.value === '' ? null : e.target.value }) }}
            disabled={tenureFieldsDisabled}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label='End Date'
            value={position.apptEndDate ?? ''}
            onChange={e => { update({ apptEndDate: e.target.value === '' ? null : e.target.value }) }}
            disabled={tenureFieldsDisabled}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={2.4}>
          <TextField
            fullWidth
            label='First Elect'
            value={position.electDate ?? ''}
            onChange={e => { update({ electDate: e.target.value === '' ? null : e.target.value }) }}
            disabled={fieldsDisabled || !electionEnabled}
            error={electionErrors.electDate != null}
            helperText={electionErrors.electDate}
          />
        </Grid>
        <Grid item xs={2.4}>
          <FormControl fullWidth disabled={fieldsDisabled || !electionEnabled} error={electionErrors.electCode != null}>
            <InputLabel id='elect-type-label'>{electTypeLabel}</InputLabel>
            <Select
              labelId='elect-type-label'
              label={electTypeLabel}
              value={position.electCode ?? ''}
              onChange={e => { update({ electCode: e.target.value === '' ? null : String(e.target.value) }) }}
            >
              <MenuItem value=''>None</MenuItem>
              {Object.entries(ElectCode).map(([code, label]) => (
                <MenuItem key={code} value={code}>{`${code} - ${label}`}</MenuItem>
              ))}
            </Select>
            {electionErrors.electCode != null && <FormHelperText>{electionErrors.electCode}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={2.4}>
          <TextField
            fullWidth
            type='number'
            label='Last Vote'
            value={position.percentVote ?? ''}
            onChange={e => { update({ percentVote: e.target.value === '' ? null : Number(e.target.value) }) }}
            disabled={fieldsDisabled || !electionEnabled}
            error={electionErrors.percentVote != null}
            helperText={electionErrors.percentVote}
            inputProps={{ step: 0.1, min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={2.4}>
          <TextField
            fullWidth
            label={`Next Elect${requiredMark}`}
            value={position.reelectYear ?? ''}
            onChange={e => { update({ reelectYear: e.target.value === '' ? null : e.target.value }) }}
            disabled={fieldsDisabled || !electionEnabled}
            error={electionErrors.reelectYear != null}
            helperText={electionErrors.reelectYear}
            inputProps={{ maxLength: 4 }}
          />
        </Grid>
        <Grid item xs={2.4}>
          <TextField
            fullWidth
            type='number'
            label={`Term Num${requiredMark}`}
            value={position.termNumber ?? ''}
            onChange={e => { update({ termNumber: e.target.value === '' ? null : Number(e.target.value) }) }}
            disabled={fieldsDisabled || !electionEnabled}
            error={electionErrors.termNumber != null}
            helperText={electionErrors.termNumber}
            inputProps={{ min: 1 }}
          />
        </Grid>
      </Grid>
      {!electionEnabled && (
        <FormHelperText sx={{ mt: 0.5 }}>
          {isElected ? 'Election fields are disabled while the position is vacant.' : 'Set Position Type to "Elected" to enable election fields.'}
        </FormHelperText>
      )}

      <Grid container spacing={2} mt={2}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label='Nominated'
            value={position.nominateDate ?? ''}
            onChange={e => { update({ nominateDate: e.target.value === '' ? null : e.target.value }) }}
            disabled={tenureFieldsDisabled}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label='Confirmed'
            value={position.confirmDate ?? ''}
            onChange={e => { update({ confirmDate: e.target.value === '' ? null : e.target.value }) }}
            disabled={tenureFieldsDisabled}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth disabled={tenureFieldsDisabled || isStatePosition}>
            <InputLabel id='level-label'>Level</InputLabel>
            <Select
              labelId='level-label'
              label='Level'
              value={isStatePosition ? '' : (position.levelId ?? '')}
              onChange={e => {
                const v = e.target.value
                update({ levelId: v === '' ? null : Number(v) })
              }}
            >
              <MenuItem value=''>None</MenuItem>
              {Object.values(EXEC_POSITION_LEVELS).map(l => (
                <MenuItem key={l.id} value={l.id}>{l.code}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth disabled={tenureFieldsDisabled || isStatePosition}>
            <InputLabel id='congressional-liaison-label'>Congressional Liaison</InputLabel>
            <Select
              labelId='congressional-liaison-label'
              label='Congressional Liaison'
              value={isStatePosition ? 0 : position.liaisonCode}
              onChange={e => { update({ liaisonCode: Number(e.target.value) as CongressionalLiaisonCode }) }}
            >
              {CONGRESSIONAL_LIAISON_OPTIONS.map(o => (
                <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth disabled={tenureFieldsDisabled || isStatePosition}>
            <InputLabel id='appointment-president-label'>Appointment President</InputLabel>
            <Select
              labelId='appointment-president-label'
              label='Appointment President'
              value={isStatePosition ? '' : (position.presidentId ?? '')}
              onChange={e => {
                const v = e.target.value
                update({ presidentId: v === '' ? null : Number(v) })
              }}
            >
              <MenuItem value=''>None</MenuItem>
              {Object.values(EXEC_APPOINTMENT_PRESIDENTS).map(p => (
                <MenuItem key={p.id} value={p.id}>{`${p.name} (${p.years})`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={6}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Responsibilities</Typography>
          <ResponsibilityMultiSelect
            disabled={tenureFieldsDisabled}
            value={position.responsibilities}
            onChange={(selectedIds) => { update({ responsibilities: selectedIds }) }}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Administrations Served</Typography>
          <AdministrationsMultiSelect
            value={position.administrationIds}
            disabled={tenureFieldsDisabled}
            onChange={(ids) => { update({ administrationIds: ids }) }}
          />
        </Grid>
      </Grid>

      {showAddressDialog && (
        <PositionAddressDialog
          open={showAddressDialog}
          roleId={generalRoleId}
          personId={personId}
          filterBuildings={(buildings) => filterBuildingsForExecutive(
            buildings,
            isStatePosition,
            position.org.topId,
            position.state?.abbrev
          )}
          onClose={() => { setShowAddressDialog(false) }}
          onSave={handleNewAddress}
        />
      )}
      {showReparentDialog && (
        <ReparentPositionDialog
          open={showReparentDialog}
          roleId={generalRoleId}
          stateId={isStatePosition ? position.state.id : null}
          currentOrgId={position.org.id}
          onClose={() => { setShowReparentDialog(false) }}
          onSelect={(org) => { update({ org }) }}
        />
      )}
    </>
  )
}
